var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { AwesomeEvent, ComponentAPI } from '@philips-internal/oswald-ui-core';
import { AbortableNavigateEventListener, ChangeEventListener, CollapseEventListener, NavigateEventListener } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { isEnumValid } from '@philips-internal/oswald-ui-utils';
import { DatePickerMode } from './date-picker-constants.js';
class DatePickerAPI extends ComponentAPI {
    constructor() {
        super(...arguments);
        /** @event */
        this.onChange = new ChangeEventListener();
        /** @event */
        this.onAfterNavigate = new NavigateEventListener();
        /** @event */
        this.onCollapse = new CollapseEventListener();
        /** @event */
        this.onNavigate = new AbortableNavigateEventListener();
    }
    /**
     * Returns the initial date.
     */
    getInitialDate() {
        return this.state.initialDate;
    }
    /**
     * Updates the initial date.
     *
     * @param initialDate - The updated initial date.
     */
    setInitialDate(initialDate) {
        this.setState({ initialDate });
    }
    /**
     * Returns the date picker data.
     */
    getData() {
        return this.state.data;
    }
    /**
     * Updates the date picker data.
     *
     * @param data - The updated data.
     */
    setData(data) {
        if (Array.isArray(data)) {
            this.setState({ data });
        }
    }
    /**
     * Returns a boolean indicating if the date picker is enabled
     */
    isEnabled() {
        return this.state.enabled;
    }
    /**
     * Sets if the date picker is enabled
     *
     * @param enabled - If the date picker is enabled
     */
    setEnabled(enabled) {
        this.setState({ enabled });
    }
    /**
     * Returns a boolean indicating if the date picker is an interval picker.
     */
    isInterval() {
        return this.state.interval;
    }
    /**
     * Sets if the date picker should be a interval picker.
     *
     * @param interval - If the date picker is an interval picker.
     */
    setInterval(interval) {
        this.setState({ interval });
    }
    /**
     * Returns a boolean indicating if the "apply" buttons is visible.
     */
    isShowApply() {
        return this.state.showApply;
    }
    /**
     * Sets if the "apply" buttons is visible.
     *
     * @param showApply - If the "apply" button should be visible.
     */
    setShowApply(showApply) {
        this.setState({ showApply });
    }
    /**
     * Returns a boolean indicating if the "clear" buttons is visible.
     */
    isShowClear() {
        return this.state.showClear;
    }
    /**
     * Sets if the "clear" buttons is visible.
     *
     * @param showClear - If the "clear" button should be visible.
     */
    setShowClear(showClear) {
        this.setState({ showClear });
    }
    /**
     * Returns a boolean indicating if the events are visible in the calendar.
     */
    isShowEvents() {
        return this.state.showEvents;
    }
    /**
     * Sets if the events should be visible in the calendar.
     *
     * @param showEvents - If the events should be visible in the calendar.
     */
    setShowEvents(showEvents) {
        this.setState({ showEvents });
    }
    /**
     * Returns the configured interval threshold.
     */
    getIntervalThreshold() {
        return this.state.intervalThreshold;
    }
    /**
     * Updates the interval threshold.
     *
     * @param intervalThreshold - The new interval threshold.
     */
    setIntervalThreshold(intervalThreshold) {
        this.setState({ intervalThreshold });
    }
    /**
     * Returns the current value of the date picker.
     */
    getValue() {
        return this.state.value;
    }
    /**
     * Updates the value of the date picker.
     *
     * @param value - The updated value.
     */
    setValue(value) {
        this.setState({ value });
    }
    /**
     * Returns the date picker mode.
     */
    getMode() {
        return this.state.mode;
    }
    /**
     * Updates the date picker mode.
     *
     * @param mode - The updated mode.
     */
    setMode(mode) {
        if (isEnumValid(DatePickerMode, mode)) {
            this.setState({ mode });
        }
    }
    /**
     * Returns the configured date ranges of the date picker.
     */
    getRanges() {
        return [...(this.state.ranges || [])];
    }
    /**
     * Updates the date ranges of the date picker.
     *
     * @param ranges - The updated ranges.
     */
    setRanges(ranges) {
        this.setState({ ranges });
    }
    /**
     * Adds a range to the date picker.
     *
     * @param startDate - The start date of the range.
     * @param endDate - The end date of the range.
     */
    addRange(startDate, endDate) {
        const ranges = this.getRanges();
        ranges.push({ endDate, startDate });
        this.setRanges(ranges);
    }
    /**
     * Adds multiple ranges to the date picker.
     *
     * @param ranges - The ranges to be added to the date picker.
     */
    addRanges(ranges) {
        const oldRanges = this.getRanges();
        this.setRanges(oldRanges.concat(ranges));
    }
    /**
     * Clears all the date ranges of the date picker.
     */
    clearRanges() {
        this.setRanges([]);
    }
    /**
     * Updates the range state of the date picker.
     *
     * @param rangeState - The updated range state.
     */
    setRangeState(rangeState) {
        this.setState({ rangeState });
    }
    /**
     * Returns the current range state of the date picker.
     */
    getRangeState() {
        return this.state.rangeState;
    }
    /**
     * Sets if the date picker is loading.
     *
     * @param loading - If the date picker is loading.
     */
    setLoading(loading) {
        this.setState({ loading });
    }
    /**
     * Returns a boolean indicating if the date picker is loading.
     */
    isLoading() {
        return this.state.loading;
    }
    /**
     * Returns a boolean indicating if the date picker expander is visible.
     */
    isShowExpander() {
        return this.state.showExpander;
    }
    /**
     * Sets if the date picker expander should be visible or not.
     *
     * @param showExpander - If the date picker expander should be visible.
     */
    setShowExpander(showExpander) {
        this.setState({ showExpander });
    }
    // Just to comply with the collapsible interface.
    isCollapsed() {
        return false;
    }
    // @ts-ignore: Just to comply with the Collapsible interface
    setCollapsed(value) {
        return;
    }
    /**
     * Returns a boolean indicating if the date picker navigation arrows are visible.
     */
    isShowNavigationArrows() {
        return this.state.showNavigationArrows;
    }
    /**
     * Sets if the date picker navigation arrows should be visible or not.
     *
     * @param showNavigationArrows - If the date picker navigation arrows should be visible.
     */
    setShowNavigationArrows(showNavigationArrows) {
        this.setState({ showNavigationArrows });
    }
}
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], DatePickerAPI.prototype, "onChange", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], DatePickerAPI.prototype, "onAfterNavigate", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], DatePickerAPI.prototype, "onCollapse", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], DatePickerAPI.prototype, "onNavigate", void 0);
export { DatePickerAPI, };
//# sourceMappingURL=date-picker-api.js.map